// ─────────────────────────────────────────────────────────────────────────────
//  UWC Shared Mixins — JavaScript-based, Lit css`` tagged template
//  Replaces SCSS _mixins.scss. Use in component styles.ts files.
// ─────────────────────────────────────────────────────────────────────────────
import { css, unsafeCSS, type CSSResult } from 'lit';
import * as t from './tokens.js';

// ── Host reset ────────────────────────────────────────────────────────────────
export const hostReset: CSSResult = css`
  :host {
    box-sizing: border-box;
  }
  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }
  :host [hidden] {
    display: none !important;
  }
`;

// ── Floating panel ────────────────────────────────────────────────────────────
//  Usage: ${floatingPanel('.panel', { durationVar: '--uwc-dd-duration', durationDefault: '140ms' })}
export function floatingPanel(
  selector: string,
  opts: {
    scaleFrom?: string;
    scaleTo?: string;
    durationVar?: string;
    durationDefault?: string;
    easing?: string;
  } = {}
): CSSResult {
  const {
    scaleFrom       = 'scale(0.96) translateY(-4px)',
    scaleTo         = 'scale(1) translateY(0)',
    durationVar     = '--uwc-panel-duration',
    durationDefault = '200ms',
    easing          = 'cubic-bezier(0.16, 1, 0.3, 1)',
  } = opts;

  const sel = unsafeCSS(selector);
  const sf  = unsafeCSS(scaleFrom);
  const st  = unsafeCSS(scaleTo);
  const dv  = unsafeCSS(durationVar);
  const dd  = unsafeCSS(durationDefault);
  const ez  = unsafeCSS(easing);

  return css`
    ${sel} {
      position: fixed;
      inset: 0 auto auto 0;
      margin: 0;
      opacity: 0;
      transform: ${sf};
      transition:
        opacity   var(${dv}, ${dd}) ${ez},
        transform var(${dv}, ${dd}) ${ez},
        display   var(${dv}, ${dd}) allow-discrete,
        overlay   var(${dv}, ${dd}) allow-discrete;
    }
    ${sel}.is-open,
    ${sel}:popover-open {
      opacity: 1;
      transform: ${st};
    }
  `;
}

// ── Placement transform-origins ────────────────────────────────────────────
export const placementOrigins: CSSResult = css`
  [data-placement^="bottom"]      { transform-origin: top center;    }
  [data-placement="bottom-start"] { transform-origin: top left;      }
  [data-placement="bottom-end"]   { transform-origin: top right;     }
  [data-placement^="top"]         { transform-origin: bottom center; }
  [data-placement="top-start"]    { transform-origin: bottom left;   }
  [data-placement="top-end"]      { transform-origin: bottom right;  }
  [data-placement^="left"]        { transform-origin: right center;  }
  [data-placement^="right"]       { transform-origin: left center;   }
`;

export const placementOriginsExtended: CSSResult = css`
  ${placementOrigins}
  [data-placement="left-start"]  { transform-origin: right top;    }
  [data-placement="left-end"]    { transform-origin: right bottom; }
  [data-placement="right-start"] { transform-origin: left top;     }
  [data-placement="right-end"]   { transform-origin: left bottom;  }
`;

// ── Focus ring ─────────────────────────────────────────────────────────────
export function focusRing(
  selector: string,
  colorVar: string     = '--uwc-color-primary',
  colorDefault: string = '#6366f1'
): CSSResult {
  return css`
    ${unsafeCSS(selector)}:focus-visible {
      outline: 2px solid var(${unsafeCSS(colorVar)}, ${unsafeCSS(colorDefault)});
      outline-offset: 2px;
    }
  `;
}

// ── Trigger wrapper (input-like controls: dropdown, datepicker) ────────────
export function triggerWrapper(
  selector: string,
  opts: {
    bgVar?:     string;
    borderVar?: string;
    radiusVar?: string;
    focusVar?:  string;
  } = {}
): CSSResult {
  const {
    bgVar     = '--uwc-trigger-bg',
    borderVar = '--uwc-trigger-border',
    radiusVar = '--uwc-trigger-radius',
    focusVar  = '--uwc-color-primary',
  } = opts;

  const sel = unsafeCSS(selector);
  const bg  = unsafeCSS(bgVar);
  const bdr = unsafeCSS(borderVar);
  const rad = unsafeCSS(radiusVar);
  const foc = unsafeCSS(focusVar);

  return css`
    ${sel} {
      display: inline-flex;
      align-items: center;
      width: 100%;
      background:    var(${bg},  ${t.surface});
      border:        var(${bdr}, 1px solid #d0cfc9);
      border-radius: var(${rad}, ${t.radiusLg});
      overflow: hidden;
      transition: border-color ${t.durationBase}, box-shadow ${t.durationBase};
    }
    ${sel}:focus-within {
      border-color: var(${foc}, ${t.primary});
      box-shadow: 0 0 0 3px color-mix(in oklab, var(${foc}, ${t.primary}) 20%, transparent);
    }
  `;
}
